import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { REGISTER } from "@/constants/testIds";
import { Eye, EyeOff } from "lucide-react";

export const RegisterPage = () => {
  const { registerWithEmail } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [fullName, setFullName] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await registerWithEmail(email, password, fullName);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: "#fcfbf9" }}>
      <Card className="w-full max-w-md rounded-2xl border-[#e5e5e5]">
        <CardHeader>
          <CardTitle className="text-3xl font-bold text-center" style={{ color: "#652D90", fontFamily: "Poppins, sans-serif" }}>
            Create Account
          </CardTitle>
          <p className="text-center text-sm" style={{ color: "#555555", fontFamily: "Poppins, sans-serif" }}>Sign up to get started</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fullName" style={{ fontFamily: "Poppins, sans-serif" }}>Full Name</Label>
              <Input
                id="fullName"
                type="text"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                data-testid={REGISTER.nameInput}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" style={{ fontFamily: "Poppins, sans-serif" }}>Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={REGISTER.emailInput}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" style={{ fontFamily: "Poppins, sans-serif" }}>Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid={REGISTER.passwordInput}
                  className="rounded-xl pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && <p className="text-sm" style={{ color: "#e90d01" }}>{error}</p>}
            <Button 
              type="submit" 
              className="w-full rounded-full" 
              disabled={loading}
              style={{ backgroundColor: "#652D90", fontFamily: "Poppins, sans-serif" }}
              data-testid={REGISTER.submitButton}
            >
              {loading ? "Creating account..." : "Sign up"}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex justify-center">
          <p className="text-sm" style={{ color: "#555555", fontFamily: "Poppins, sans-serif" }}>
            Already have an account?{" "}
            <Link to="/login" className="underline" style={{ color: "#652D90" }} data-testid={REGISTER.loginLink}>
              Sign in
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};
